import { Form } from 'rsuite';
import { RsButton } from 'components/Rsuite/Button';
import WarningRoundIcon from '@rsuite/icons/WarningRound';
import useCreativeStore from 'store/creativeStore';
import { useProductStore } from 'store/productStore';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import i18n from 'i18n';
import { useCampaignStore } from 'store/campaignStore';
import { useFormik } from 'formik';
import { useOrderStore } from 'store/orderStore';
import {
  BindingManager,
  DefaultBindingManager,
} from 'core/binding/BindingManager';
import {
  CreativeManager,
  DefaultCreativeManager,
} from 'core/creative/CreativeManager';
import { useEffect, useMemo, useState } from 'react';
import { L1ObjectChannel } from 'core/l1Object/L1Object';
import useCreativeForm from '../Hooks/useCreativeForm';
import { toast } from 'react-toastify';

const creativeManager: CreativeManager = new DefaultCreativeManager();
const bindingManager: BindingManager = new DefaultBindingManager();

const ProductNativeForm = ({ campaignId }) => {
  const { order } = useOrderStore();
  const { openProductManager, handleLoading } = useCampaignStore();
  const {
    actionType,
    creative,
    creatives,
    openCreativeManager,
    handleChangeCreative,
  } = useCreativeStore();
  const { selectedProducts, changeSelectedProducts, resetProductStore } =
    useProductStore();
  const { getCreativesByCampaignId } = useCreativeForm();
  // 新增素材後端回傳的素材資料，後續要綁定活動需要素材ID
  const [newCreative, setNewCreative] = useState<any>();
  // 根據actionType來定義初始值
  const initialValues =
    actionType === 'edit'
      ? {
          ...creative,
          typeProperties: {},
        }
      : {
          products: [],
          typeProperties: {
            product: {},
          },
        };
  // 初始化formik
  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: (values) => {
      switch (actionType) {
        case 'create':
          handleLoading(true);
          handleCreateCreative();
          break;
        case 'edit':
          break;
        default:
          break;
      }
    },
  });
  // 開啟選品模組
  const handleOpenProductManager = () => {
    openProductManager(true);
  };
  // 刪除已選商品
  const deleteProduct = (productId) => {
    changeSelectedProducts((products) =>
      products.filter((product) => product.productId !== productId),
    );
  };
  // 驗證商品是否已綁定過此活動
  const faultyProductsMap = useMemo(() => {
    return selectedProducts.reduce((acc, product) => {
      const creativeType_9 =
        creatives?.filter((creative) => creative.creativeType === 9) || [];
      // 原生商品素材 不可重複綁定
      const isRepet = creativeType_9?.find(
        (creative) => creative.product.productId === product['productId'],
      )
        ? true
        : false;
      if (isRepet) {
        acc[product.name] = '該活動已綁定過此原生商品素材˙，請重新選擇';
      }
      return acc;
    }, {});
  }, [creatives, selectedProducts]);
  // 取得已綁定過的商品名稱
  const faultyKeywords = useMemo(
    () => Object.keys(faultyProductsMap),
    [faultyProductsMap],
  );
  // 新增素材
  const handleCreateCreative = async () => {
    try {
      const creativeJson = formik.values.products.map((product) => ({
        creative: {
          creativeType: 9,
          advertiserId: order.advertiserId,
          tenmaxCategory: 'e_commerce',
          typeProperties: {
            product: JSON.stringify(product),
          },
        },
      }));
      const data = await creativeManager.createCreativesByJson(creativeJson);
      if (data) {
        setNewCreative(data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };
  // 綁定素材
  const handleBindCreative = async () => {
    try {
      const creativeIds = newCreative.map((creative) =>
        creative.creative.creativeId.toString(),
      );
      const bind = await bindingManager.createBinding(L1ObjectChannel.RTB, [
        {
          l2ChannelIds: [campaignId],
          active: true,
          creativeIds: creativeIds,
        },
      ]);
      console.log('bind', bind);
      toast.success('素材建立成功');
      getCreativesByCampaignId(campaignId);
      openCreativeManager(false);
    } catch (error) {
      console.error(error);
    } finally {
      handleLoading(false);
    }
  };

  // component 卸載時
  // 1. 要清空正在編輯的 creative
  // 2. 清空 主打品(product), 因為其他素材類型也有 product
  useEffect(() => {
    return () => {
      formik.resetForm();
      resetProductStore();
      handleChangeCreative(null);
    };
  }, []);

  useEffect(() => {
    formik.setFieldValue('products', selectedProducts);
  }, [selectedProducts]);

  useEffect(() => {
    if (newCreative) {
      handleBindCreative();
    }
  }, [newCreative]);

  return (
    <>
      <Form.Group controlId='product'>
        <Form.ControlLabel>商品</Form.ControlLabel>
        {selectedProducts.length > 0 && actionType === 'create' && (
          <div className='rs-form-control-wrapper'>
            <div className='flex items-start gap-2'>
              <div className='float-left flex max-h-[400px] w-[420px] flex-wrap gap-2 overflow-y-auto rounded border border-gray-300 px-2 py-3'>
                {selectedProducts.map((product) => (
                  <span
                    key={product.productId}
                    className='inline-block break-words border border-[#98969d] bg-[#f7f7f7] p-2 text-[#242328]'
                  >
                    {product.name}
                    <WarningRoundIcon
                      className='ml-1 cursor-pointer text-[#413993]'
                      onClick={() => deleteProduct(product.productId)}
                    />
                  </span>
                ))}
              </div>
              <div>
                <IconWithTooltip
                  icon={faPencilAlt}
                  tooltipProps={{
                    id: `select-product-field-edit-products-hint`,
                    tooltip: i18n.t<string>(
                      'creativeSetupFlow.labels.editProductsHint',
                    ),
                  }}
                  onClick={handleOpenProductManager}
                />
              </div>
            </div>
            <Form.ErrorMessage
              show={faultyKeywords.length !== 0}
              placement='bottomStart'
            >
              <div className='h-auto w-max max-w-[300px] overflow-auto text-wrap'>
                <p className='font-bold'>
                  該活動已綁定過以下原生商品素材，請重新選擇
                </p>
                <div className='flex flex-col gap-2'>
                  {faultyKeywords.map((keyword, index) => (
                    <span key={keyword}>
                      {index + 1}. {keyword}
                    </span>
                  ))}
                </div>
              </div>
            </Form.ErrorMessage>
          </div>
        )}
        {selectedProducts.length === 0 && (
          <RsButton
            appearance='ghost'
            className='!border-[#615f67] !text-[#615f67]'
            onClick={handleOpenProductManager}
            errorMessage={selectedProducts.length === 0 ? '尚未選擇商品' : ''}
          >
            選擇商品
          </RsButton>
        )}
      </Form.Group>
      <div className='pb-4 pt-10'>
        <div className='flex items-center justify-start gap-4'>
          <RsButton
            appearance='primary'
            onClick={() => formik.handleSubmit()}
            disabled={
              selectedProducts.length === 0 || faultyKeywords.length !== 0
            }
          >
            <span className='text-[16px]'>完成</span>
          </RsButton>
        </div>
      </div>
    </>
  );
};

export default ProductNativeForm;
